export type AgentId = 'pyre' | 'hoard' | 'rain' | 'tide';
export const AGENT_IDS: readonly AgentId[] = ['pyre', 'hoard', 'rain', 'tide'];

/** Index of each agent in the on-chain `uint16[4]` / `address[4]` arrays. */
export const AGENT_INDEX: Record<AgentId, number> = { pyre: 0, hoard: 1, rain: 2, tide: 3 };

export type ActionSize = 'WAIT' | 'SMALL' | 'MEDIUM' | 'LARGE';
export const ACTIONS: readonly ActionSize[] = ['WAIT', 'SMALL', 'MEDIUM', 'LARGE'];

/** What every agent perceives about the market on each decision. */
export interface MarketSnapshot {
  /** Unix time in ms. */
  t: number;
  block?: number;
  graduated: boolean;
  /** 0..1 progress toward the bonding-curve graduation threshold. */
  curveProgress: number;
  tokenAgeSec: number;
  ethUsd: number;
  priceEth: number;
  totalSupply: number;
  burned: number;
  circulating: number;
  mcapEth: number;
  /** ETH side of the active market (curve real reserve or V4 pool). */
  liquidityEth: number;
  liquidityTokens: number;
  change5m: number;
  change1h: number;
  change6h: number;
  change24h: number;
  /** Realised volatility of 1-minute log returns over the last hour, annualisation-free. */
  volatility1h: number;
  high24h: number;
  low24h: number;
  volume1hEth: number;
  volume24hEth: number;
  buyVolume1hEth: number;
  sellVolume1hEth: number;
  trades1h: number;
  holders: number;
  holders1hAgo: number;
  holders24hAgo: number;
  /** Creator-fee ETH that reached the treasury over the last 24h. */
  feesEth24h: number;
}

export interface VaultState {
  agent: AgentId;
  address: string;
  ethBalance: number;
  tokenBalance: number;
  totalEthIn: number;
  totalEthSpent: number;
  totalTokens: number;
  actionCount: number;
  lastActionAt: number;
  /** Role-specific counters, e.g. totalBurned, totalDistributed, positionLiquidity. */
  extra: Record<string, number>;
}

export interface TreasuryState {
  address: string;
  ethBalance: number;
  escrowPending: number;
  totalHarvested: number;
  /** Basis points per agent, in AGENT_IDS order. */
  weights: number[];
  routed: number[];
  lastHarvestAt: number;
  lastRebalanceAt: number;
  minWeightBps: number;
  maxWeightBps: number;
  maxShiftBps: number;
  minRebalanceIntervalSec: number;
}

export interface ActionPlan {
  agent: AgentId;
  size: ActionSize;
  ethIn: number;
}

export interface ActionResult {
  ok: boolean;
  error?: string;
  txHashes: string[];
  ethSpent: number;
  tokensAcquired: number;
  /** Average execution price in ETH per token. */
  execPrice: number;
  priceBefore: number;
  priceAfter: number;
  viaPool: boolean;
  tokensBurned?: number;
  recipients?: number;
  roundId?: number;
  liquidityAdded?: number;
  ethToLiquidity?: number;
  tokensToLiquidity?: number;
  gasEth?: number;
}

export interface HarvestResult {
  txHash: string;
  claimed: number;
  total: number;
  amounts: number[];
}

/** Port the protocol uses to see and touch the chain (live or simulated). */
export interface ChainPort {
  readonly mode: 'sim' | 'live';
  now(): number;
  snapshot(): Promise<MarketSnapshot>;
  vault(agent: AgentId): Promise<VaultState>;
  treasury(): Promise<TreasuryState>;
  harvest(): Promise<HarvestResult | null>;
  setWeights(weightsBps: number[]): Promise<string>;
  execute(plan: ActionPlan): Promise<ActionResult>;
  /** Share (0..1) of airdropped tokens from a round still held by recipients. */
  retention?(roundId: number): Promise<number | null>;
  explorerTx(hash: string): string;
  explorerAddress(address: string): string;
}

export interface ScoredAction {
  action: ActionSize;
  mean: number;
  std: number;
  instinct: number;
  total: number;
  available: boolean;
}

export interface Signal {
  feature: string;
  label: string;
  value: number;
  weight: number;
}

export interface Thought {
  summary: string;
  regime: string;
  confidence: number;
  scores: ScoredAction[];
  signals: Signal[];
  genomeId: number;
  instinctWeight: number;
  explored: boolean;
}

export interface Lesson {
  id: string;
  text: string;
  confidence: number;
  support: number;
  effect: number;
  discoveredAt: number;
}

export type ProtocolEvent =
  | {
      type: 'action';
      id: string;
      t: number;
      agent: AgentId;
      size: ActionSize;
      ethIn: number;
      result: ActionResult;
      thought: Thought;
    }
  | { type: 'wait'; id: string; t: number; agent: AgentId; reason: string; thought: Thought }
  | {
      type: 'learn';
      id: string;
      t: number;
      agent: AgentId;
      decisionId: string;
      size: ActionSize;
      reward: number;
      predicted: number;
    }
  | { type: 'harvest'; id: string; t: number; result: HarvestResult }
  | {
      type: 'council';
      id: string;
      t: number;
      weights: number[];
      previous: number[];
      efficiency: Record<AgentId, number>;
      rationale: string;
      txHash: string;
    }
  | {
      type: 'evolution';
      id: string;
      t: number;
      agent: AgentId;
      generation: number;
      championId: number;
      replacedId: number;
      childId: number;
    }
  | { type: 'lesson'; id: string; t: number; agent: AgentId; lesson: Lesson }
  | { type: 'graduation'; id: string; t: number }
  | { type: 'error'; id: string; t: number; agent?: AgentId; message: string };

export type ProtocolEventType = ProtocolEvent['type'];
